import { z } from 'zod';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

/**
 * Meta entity ids (WABA, phone number) are numeric Graph node ids. Bounding
 * them here keeps a hostile value out of the Graph URL paths the provisioning
 * pipeline builds from them. Exported so the web client sanitizes the popup's
 * postMessage payload against the same rule the wire contract enforces.
 */
export const META_ENTITY_ID_PATTERN = /^\d{1,32}$/;

export const metaEntityId = z
  .string()
  .regex(META_ENTITY_ID_PATTERN, 'must be a numeric Meta entity id');

export const whatsappCallbackBody = z.object({
  state: z.string().min(1, 'state is required'),
  code: z.string().optional(),
  access_token: z.string().optional(),
  user_id: z.string().optional(),
  /**
   * The phone number and WABA the operator actually picked in Meta's Embedded
   * Signup popup (posted to the opener as `WA_EMBEDDED_SIGNUP`). Optional:
   * older clients, and the redirect-based flow, never see that event and fall
   * back to discovery.
   */
  phone_number_id: metaEntityId.optional(),
  waba_id: metaEntityId.optional(),
}).refine(
  (data) => !!data.access_token || !!data.code,
  { message: 'Missing required parameters: state and either access_token or code' }
);

// =============================================================================
// AUTH ME — GET /api/auth/me
// =============================================================================

export const authMeResponse = z.object({
  user_id: z.string(),
  email: z.string(),
  org_id: z.string().nullable(),
  is_platform_admin: z.boolean(),
  // Present so the MCP server can conversation-scope + harness-gate the wayai-conversation
  // control surface (harness-agents PR 2.7c): session_id = conversation_id for a harness
  // token; is_harness marks the synthetic in-sandbox principal. Optional/back-compat.
  session_id: z.string().optional(),
  is_harness: z.boolean().optional(),
});

export type AuthMeResponse = z.infer<typeof authMeResponse>;

// =============================================================================
// WS TICKET — POST /api/auth/ws-ticket
// =============================================================================

export const wsTicketResponse = z.object({
  ticket: z.string(),
  /** Seconds until the ticket expires if unconsumed. */
  expires_in: z.number(),
});

export type WsTicketResponse = z.infer<typeof wsTicketResponse>;

// =============================================================================
// SESSION CHECK — GET /api/auth/session-check
// =============================================================================

export const sessionCheckResponse = z.object({
  valid: z.literal(true),
});

export type SessionCheckResponse = z.infer<typeof sessionCheckResponse>;

// =============================================================================
// LOGOUT — POST /api/auth/logout
// =============================================================================

export const logoutResponse = z.object({
  success: z.literal(true),
});

export type LogoutResponse = z.infer<typeof logoutResponse>;

// =============================================================================
// MAGIC CODE — POST /api/auth/magic-code/send + /verify (native mobile login)
// =============================================================================

export const magicCodeSendBody = z.object({
  email: z.string().email('Invalid email').max(254), // RFC 5321 max; bounds the request payload
});

// Uniform response whether or not the email has an account (anti-enumeration).
export const magicCodeSendResponse = z.object({
  success: z.literal(true),
});
export type MagicCodeSendResponse = z.infer<typeof magicCodeSendResponse>;

export const magicCodeVerifyBody = z.object({
  email: z.string().email('Invalid email').max(254), // RFC 5321 max; bounds the request payload
  code: z.string().min(1, 'Code is required').max(12), // WorkOS sends a 6-digit code; bound the input
});

// Same token shape the OAuth flow stores — WorkOS User Management access + refresh tokens.
export const magicCodeVerifyResponse = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
});
export type MagicCodeVerifyResponse = z.infer<typeof magicCodeVerifyResponse>;

// =============================================================================
// PASSWORD LOGIN — WorkOS password auth. TWO consumers, both of which a schema change
// here must be evaluated against:
//   - native  POST /api/auth/password/login   (apps/backend/src/routes/api/auth-me.ts)
//   - web     POST /api/auth/password-login   (apps/web/src/app/api/auth/password-login)
// =============================================================================
// Exists because the app stores (Apple 2.1a, Google Play sign-in requirements) demand
// typeable, reusable, non-expiring reviewer credentials — which a single-use magic code
// can never be. Meta's data-access renewal needs the same on the web. Login only: there is
// deliberately no in-app set/reset flow, so a password exists only on accounts explicitly
// given one in the WorkOS dashboard (the review accounts).
//
// Every constraint here is load-bearing for BOTH routes, not cosmetic:
//   - `password.max(128)` keeps an arbitrarily large string from reaching WorkOS.
//   - `email.email()` + `.max(254)` bound what can become a rate-limit bucket key. Both
//     routes key their per-email brute-force caps on this exact field — the native one
//     through `magicCodeEmailLimited`'s `hashToken(email.trim().toLowerCase())`
//     (auth-me.ts), the web one through `getEmailIdentifier`. Relaxing the email field
//     (admitting a non-email identifier, dropping `.email()`) widens what both caps key
//     on; neither route is "additional" to the other here.
// Raising or removing any of them changes both routes' input surface.

export const passwordLoginBody = z.object({
  email: z.string().email('Invalid email').max(254), // RFC 5321 max; bounds the request payload
  password: z.string().min(1, 'Password is required').max(128), // bound the payload; WorkOS validates
});

// Same token pair as the magic-code + OAuth flows.
export const passwordLoginResponse = magicCodeVerifyResponse;
export type PasswordLoginResponse = z.infer<typeof passwordLoginResponse>;

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type WhatsappCallbackInput = z.infer<typeof whatsappCallbackBody>;
